import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Copy, ExternalLink, Search, Filter, Download } from "lucide-react";
import { PRODUCT_REFERENCE, ProductReference, PRODUCT_CATEGORIES, PRICE_RANGES } from "@/data/productReference";
import { toast } from "sonner";

interface ProductReferenceTableProps {
  showInternalCodes?: boolean;
  allowExport?: boolean;
}

export const ProductReferenceTable = ({ 
  showInternalCodes = true,
  allowExport = true 
}: ProductReferenceTableProps) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState<string>("all");
  const [priceFilter, setPriceFilter] = React.useState<string>("all");

  const products = Object.values(PRODUCT_REFERENCE);

  // Filter products based on search and filters
  const filteredProducts = products.filter(product => {
    const matchesSearch = searchTerm === "" || 
      product.avtName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.internalCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.factoryModel.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
    const matchesPrice = priceFilter === "all" || product.priceRange === priceFilter;
    
    return matchesSearch && matchesCategory && matchesPrice;
  });

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const exportToCSV = () => {
    const headers = [
      "AVT Name", "Internal Code", "Factory Model", "Logistics Code", 
      "Category", "Price Range", "Warranty", "URL Slug"
    ];
    
    const csvContent = [
      headers.join(","),
      ...filteredProducts.map(product => [
        product.avtName,
        product.internalCode,
        product.factoryModel,
        product.logisticsCode,
        product.category,
        product.priceRange,
        product.specifications.warranty,
        product.urlSlug
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AVT_Product_Reference_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Product reference exported to CSV");
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              AVT Product Reference Sheet
            </CardTitle>
            <CardDescription>
              Master reference for all products with cross-reference codes for logistics and shipping
            </CardDescription>
          </div>
          {allowExport && (
            <Button 
              onClick={exportToCSV} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="table" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="table">Reference Table</TabsTrigger>
            <TabsTrigger value="quick">Quick Lookup</TabsTrigger>
          </TabsList>
          
          <TabsContent value="table" className="space-y-4">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, internal code, or factory model..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {PRODUCT_CATEGORIES.map(category => (
                    <SelectItem key={category} value={category}>
                      {category.replace('-', ' ').toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priceFilter} onValueChange={setPriceFilter}>
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue placeholder="Filter by price" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Prices</SelectItem>
                  {PRICE_RANGES.map(price => (
                    <SelectItem key={price} value={price}>
                      {price.toUpperCase()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Results count */}
            <div className="text-sm text-muted-foreground">
              Showing {filteredProducts.length} of {products.length} products
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Info</TableHead>
                    {showInternalCodes && (
                      <>
                        <TableHead>Internal Code</TableHead>
                        <TableHead>Factory Model</TableHead>
                        <TableHead>Logistics Code</TableHead>
                      </>
                    )}
                    <TableHead>Category</TableHead>
                    <TableHead>Price Range</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProducts.map((product) => (
                    <TableRow key={product.avtName}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{product.avtName}</div>
                          <div className="text-sm text-muted-foreground">
                            {product.tagline}
                          </div>
                        </div>
                      </TableCell>
                      
                      {showInternalCodes && (
                        <>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(product.internalCode, "Internal Code")}
                              className="font-mono text-xs h-auto p-1"
                            >
                              {product.internalCode}
                              <Copy className="w-3 h-3 ml-1" />
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(product.factoryModel, "Factory Model")}
                              className="font-mono text-xs h-auto p-1"
                            >
                              {product.factoryModel}
                              <Copy className="w-3 h-3 ml-1" />
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => copyToClipboard(product.logisticsCode, "Logistics Code")}
                              className="font-mono text-xs h-auto p-1"
                            >
                              {product.logisticsCode}
                              <Copy className="w-3 h-3 ml-1" />
                            </Button>
                          </TableCell>
                        </>
                      )}
                      
                      <TableCell>
                        <Badge variant="secondary" className="text-xs">
                          {product.category.replace('-', ' ')}
                        </Badge>
                      </TableCell>
                      
                      <TableCell>
                        <Badge 
                          variant={product.priceRange === 'ultra-luxury' ? 'default' : 'outline'}
                          className="text-xs"
                        >
                          {product.priceRange}
                        </Badge>
                      </TableCell>
                      
                      <TableCell>
                        {product.pageComponent && (
                          <Button
                            variant="ghost"
                            size="sm"
                            asChild
                          >
                            <a href={product.urlSlug} target="_blank" rel="noopener noreferrer">
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="quick" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProducts.map((product) => (
                <Card key={product.avtName} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{product.avtName}</h3>
                        <Badge variant="outline" className="text-xs">
                          {product.category.replace('-', ' ')}
                        </Badge>
                      </div>
                      
                      <p className="text-sm text-muted-foreground">
                        {product.tagline}
                      </p>
                      
                      {showInternalCodes && (
                        <div className="space-y-1 pt-2 border-t border-muted">
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            <span className="text-muted-foreground">Internal:</span>
                            <code className="font-mono text-right">{product.internalCode}</code>
                            
                            <span className="text-muted-foreground">Factory:</span>
                            <code className="font-mono text-right">{product.factoryModel}</code>
                            
                            <span className="text-muted-foreground">Logistics:</span>
                            <code className="font-mono text-right">{product.logisticsCode}</code>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center pt-2">
                        <Badge variant="secondary" className="text-xs">
                          {product.priceRange}
                        </Badge>
                        {product.pageComponent && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={product.urlSlug}>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};